import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { solicitudesAPI, talleresAPI, pagosAPI, paisesAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { translations } from '../../utils/translations';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, CheckCircle, CheckCircle2, ArrowLeft, FileText, Mail, ArrowUp, CreditCard, Plane, Building2, Search, XCircle, AlertCircle } from 'lucide-react';
import TerminosCondicionesModal from '../../components/ui/TerminosCondicionesModal';
import MercadoPagoCheckout from '../../components/MercadoPagoCheckout';
import FormularioTallerItinerante from '../../components/FormularioTallerItinerante';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { formatApiError } from '../../utils/apiError';

// Brand logo - Nueva imagen C&M 2024
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_5612798b-7eab-4f57-bce1-b7ba348a317b/artifacts/qgvhhx6f_fav%202.jpeg';

const SuscripcionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showTerminos, setShowTerminos] = useState(false);
  const [terminosAceptados, setTerminosAceptados] = useState(false);
  const [isUpgradeMode, setIsUpgradeMode] = useState(false);
  const [tallerActual, setTallerActual] = useState(null);
  const [loadingTaller, setLoadingTaller] = useState(false);
  const [showFormularioItinerante, setShowFormularioItinerante] = useState(false);
  
  // Estados para autocompletado de talleres existentes
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedExistingTaller, setSelectedExistingTaller] = useState(null);
  const searchTimeoutRef = useRef(null);
  
  // Estados para verificación de ANAC
  const [anacValidation, setAnacValidation] = useState({
    status: 'idle', // 'idle', 'checking', 'valid', 'invalid', 'has_subscription', 'multiple', 'mismatch'
    tallerInfo: null,        // Compat con código viejo (1 sólo taller)
    talleresMultiples: [],   // Si hay >1 taller con el mismo ANAC (satélites)
  });
  const anacTimeoutRef = useRef(null);

  // Países dinámicos desde DB
  const [paisesDB, setPaisesDB] = useState([]);
  useEffect(() => {
    paisesAPI.getActivos()
      .then(r => setPaisesDB(r.data.items || []))
      .catch(() => setPaisesDB([]));
  }, []);
  
  const [formData, setFormData] = useState({
    numero_anac: '',
    razon_social: '',
    pais: 'Argentina',
    provincia: '',
    nivel_solicitado: 'status_report',
    nombre_contacto: '',
    email_contacto: '',
    telefono_contacto: '',
    ubicacion_completa: '',
    notas: ''
  });

  // Textos traducidos
  const txt = translations.suscripcion;
  const txtHome = translations.home;

  // Detectar modo upgrade y cargar datos del taller
  useEffect(() => {
    const upgrade = searchParams.get('upgrade');
    if (upgrade === 'true' && user?.taller_id) {
      setIsUpgradeMode(true);
      loadTallerData(user.taller_id);
    }
  }, [searchParams, user]);

  const loadTallerData = async (tallerId) => {
    setLoadingTaller(true);
    try {
      const response = await talleresAPI.getOne(tallerId);
      const taller = response.data;
      setTallerActual(taller);
      
      // Pre-rellenar formulario con datos del taller
      setFormData(prev => ({
        ...prev,
        numero_anac: taller.numero_anac || '',
        razon_social: taller.razon_social || '',
        pais: taller.ubicacion?.pais || 'Argentina',
        provincia: taller.ubicacion?.provincia || '',
        nombre_contacto: taller.titular?.nombre || user?.nombre || '',
        email_contacto: taller.titular?.email || user?.email || '',
        telefono_contacto: taller.titular?.telefono || '',
        ubicacion_completa: taller.ubicacion?.direccion_completa || '',
        // Pre-seleccionar el siguiente nivel disponible
        nivel_solicitado: getNextLevel(taller.nivel_suscripcion)
      }));
      
      // Auto-aceptar términos para upgrades (ya los aceptaron antes)
      setTerminosAceptados(true);
    } catch (error) {
      console.error('Error cargando datos del taller:', error);
      toast.error('Error al cargar los datos de tu taller');
      setIsUpgradeMode(false);
    } finally {
      setLoadingTaller(false);
    }
  };

  const getNextLevel = (currentLevel) => {
    if (currentLevel === 'status_report') return 'estandar';
    if (currentLevel === 'estandar') return 'estandar_pro';
    if (currentLevel === 'estandar_pro') return 'premium';
    return 'premium';
  };

  const getNivelLabel = (nivel) => {
    const labels = {
      'status_report': 'Status Report',
      'estandar': 'Standard',
      'estandar_pro': 'Standard Pro',
      'premium': 'Premium'
    };
    return labels[nivel] || nivel;
  };

  // Preseleccionar nivel si viene en la URL
  useEffect(() => {
    const nivel = searchParams.get('nivel');
    if (nivel && ['status_report', 'estandar', 'estandar_pro', 'premium'].includes(nivel)) {
      setFormData(prev => ({ ...prev, nivel_solicitado: nivel }));
    }
  }, [searchParams]);

  // Cambiar país a Brasil si el idioma es portugués
  useEffect(() => {
    if (language === 'pt') {
      setFormData(prev => ({ ...prev, pais: 'Brasil' }));
    }
  }, [language]);

  // Función de búsqueda de talleres con debounce
  const searchTalleres = useCallback(async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      setShowAutocomplete(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await talleresAPI.buscarSinSuscripcion(query);
      setSearchResults(response.data?.data || []);
      setShowAutocomplete(true);
    } catch (error) {
      console.error('Error buscando talleres:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handler para el campo razón social con debounce
  const handleRazonSocialChange = (value) => {
    setFormData(prev => ({ ...prev, razon_social: value }));
    
    // Si ya había un taller seleccionado, limpiar la selección
    if (selectedExistingTaller) {
      setSelectedExistingTaller(null);
    }
    
    // Cancelar búsqueda anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce de 400ms
    searchTimeoutRef.current = setTimeout(() => {
      searchTalleres(value);
    }, 400);
  };

  // Función para seleccionar un taller existente del autocompletado
  const handleSelectTaller = (taller) => {
    const anacAnterior = (formData.numero_anac || '').trim();
    const anacNuevo = (taller.numero_anac || '').trim();
    const normAnac = (s) => (s || '').toUpperCase().replace(/[\s-]/g, '');

    setSelectedExistingTaller(taller);
    setShowAutocomplete(false);
    setSearchResults([]);

    // Auto-rellenar el formulario con los datos del taller
    setFormData(prev => ({
      ...prev,
      razon_social: taller.razon_social || '',
      numero_anac: taller.numero_anac || '',
      pais: taller.ubicacion?.pais || 'Argentina',
      provincia: taller.ubicacion?.provincia || '',
      nombre_contacto: taller.titular?.nombre || '',
      email_contacto: taller.titular?.email || '',
      telefono_contacto: taller.titular?.telefono || '',
      ubicacion_completa: taller.ubicacion?.direccion_completa || ''
    }));

    // Resetear la validación ANAC (porque acabamos de seleccionar a mano)
    setAnacValidation({
      status: 'valid',
      tallerInfo: { ...taller, tiene_suscripcion: false },
      talleresMultiples: [],
    });

    // Si el usuario tenía tipeado un ANAC distinto, avisamos sutilmente
    if (anacAnterior && normAnac(anacAnterior) !== normAnac(anacNuevo)) {
      toast.info(`Se actualizó el número ANAC al del taller seleccionado: ${anacNuevo}`);
    } else {
      toast.success('Datos del taller cargados. Complete los campos faltantes.');
    }
  };

  // Función para deseleccionar el taller existente
  const handleClearSelection = () => {
    setSelectedExistingTaller(null);
    setAnacValidation({ status: 'idle', tallerInfo: null });
    setFormData(prev => ({
      ...prev,
      razon_social: '',
      numero_anac: '',
      nombre_contacto: '',
      email_contacto: '',
      telefono_contacto: '',
      ubicacion_completa: ''
    }));
  };

  // Función de verificación de ANAC con debounce — maneja 1 match, múltiples, o ninguno
  const verifyAnac = useCallback(async (anac) => {
    if (anac.length < 2) {
      setAnacValidation({ status: 'idle', tallerInfo: null, talleresMultiples: [] });
      return;
    }

    setAnacValidation({ status: 'checking', tallerInfo: null, talleresMultiples: [] });
    try {
      const response = await talleresAPI.verificarAnac(anac);
      const talleres = response.data?.talleres || (response.data?.taller ? [response.data.taller] : []);

      if (!response.data?.existe || talleres.length === 0) {
        setAnacValidation({ status: 'invalid', tallerInfo: null, talleresMultiples: [] });
        return;
      }

      // Múltiples matches → el usuario debe elegir
      if (talleres.length > 1) {
        setAnacValidation({ status: 'multiple', tallerInfo: null, talleresMultiples: talleres });
        return;
      }

      // 1 sólo match
      const taller = talleres[0];
      if (taller.tiene_suscripcion) {
        setAnacValidation({ status: 'has_subscription', tallerInfo: taller, talleresMultiples: [] });
        // Si el usuario ya había seleccionado este taller por razón social, deseleccionar (no aplica)
        return;
      }

      setAnacValidation({ status: 'valid', tallerInfo: taller, talleresMultiples: [] });

      // AUTO-LINKEO: si todavía no eligió un taller manualmente, linkeamos solo si la razón social
      // tipeada coincide (o todavía está vacía → la rellenamos).
      const normalize = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ').replace(/[.,;:_-]/g, '');
      const rsTipeada = normalize(formData.razon_social);
      const rsTaller = normalize(taller.razon_social);
      if (!selectedExistingTaller) {
        if (!rsTipeada || rsTipeada === rsTaller) {
          // Auto-linkear: setear el taller y rellenar razón social si está vacía
          setSelectedExistingTaller(taller);
          setFormData(prev => ({
            ...prev,
            razon_social: prev.razon_social || taller.razon_social || '',
          }));
        } else {
          // Razón social tipeada NO coincide con el ANAC → bandera "mismatch"
          setAnacValidation({ status: 'mismatch', tallerInfo: taller, talleresMultiples: [] });
        }
      }
    } catch (error) {
      console.error('Error verificando ANAC:', error);
      setAnacValidation({ status: 'idle', tallerInfo: null, talleresMultiples: [] });
    }
  }, [formData.razon_social, selectedExistingTaller]);

  // Handler para el campo ANAC con debounce
  const handleAnacChange = (value) => {
    setFormData(prev => ({ ...prev, numero_anac: value }));
    
    // Cancelar verificación anterior
    if (anacTimeoutRef.current) {
      clearTimeout(anacTimeoutRef.current);
    }
    
    // Debounce de 500ms
    anacTimeoutRef.current = setTimeout(() => {
      verifyAnac(value);
    }, 500);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (anacTimeoutRef.current) {
        clearTimeout(anacTimeoutRef.current);
      }
    };
  }, []);

  const getProvincias = () => {
    const p = paisesDB.find(x => x.nombre === formData.pais);
    return p?.provincias || [];
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Si hay un taller existente seleccionado, incluir su ID
      const submitData = {
        ...formData,
        taller_existente_id: selectedExistingTaller?.id || null
      };
      
      await solicitudesAPI.crearSuscripcion(submitData);
      setSubmitted(true);
      toast.success(language === 'pt' ? 'Solicitação enviada com sucesso' : 'Solicitud enviada correctamente');
    } catch (error) {
      toast.error(formatApiError(error, 'Error'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center shadow-xl border-0">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-br from-teal-100 to-teal-200 p-4 rounded-full">
                <Mail className="h-12 w-12 text-teal-600" />
              </div>
            </div>
            <CardTitle className="text-2xl text-slate-800">{t(txt.solicitudEnviada)}</CardTitle>
            <CardDescription className="text-slate-600">
              {t(txt.solicitudDesc)}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              {t(txt.revistaSpam)}
            </div>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full bg-teal-500 hover:bg-teal-600"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t(txt.volverInicio)}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Modo Upgrade: Formulario simplificado para talleres existentes
  if (isUpgradeMode) {
    if (loadingTaller) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
        <div className="max-w-lg mx-auto">
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-2">
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-br from-teal-100 to-teal-200 p-4 rounded-full">
                  <ArrowUp className="h-10 w-10 text-teal-600" />
                </div>
              </div>
              <CardTitle className="text-2xl text-slate-800">Actualizar Plan</CardTitle>
              <CardDescription>
                Solicita el cambio de tu suscripción
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Datos del taller (solo lectura) */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-slate-700 mb-3">Tu Taller</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-slate-500">Razón Social:</span> <span className="font-medium">{formData.razon_social}</span></p>
                  <p><span className="text-slate-500">ANAC:</span> <span className="font-medium">{formData.numero_anac}</span></p>
                  <p><span className="text-slate-500">Ubicación:</span> <span className="font-medium">{formData.provincia}, {formData.pais}</span></p>
                  <p><span className="text-slate-500">Plan Actual:</span> <span className="font-medium text-slate-600">{getNivelLabel(tallerActual?.nivel_suscripcion)}</span></p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Selección de nuevo nivel */}
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">Nuevo Plan</Label>
                  <RadioGroup
                    value={formData.nivel_solicitado}
                    onValueChange={(value) => handleChange('nivel_solicitado', value)}
                    className="grid gap-3"
                  >
                    {tallerActual?.nivel_suscripcion === 'status_report' && (
                      <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl transition-all cursor-pointer ${
                        formData.nivel_solicitado === 'estandar' 
                          ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20' 
                          : 'border-teal-200 hover:border-teal-300 bg-teal-50/30'
                      }`}>
                        <RadioGroupItem value="estandar" id="estandar-upgrade" className="text-teal-500" />
                        <Label htmlFor="estandar-upgrade" className="flex-1 cursor-pointer">
                          <span className="font-semibold text-slate-800">Standard</span>
                          <span className="text-teal-600 ml-2 font-bold">USD $120/mes</span>
                          <p className="text-sm text-slate-500 mt-1">2 usuarios, documentación técnica</p>
                          <p className="text-xs text-green-600 mt-1 font-medium">💳 Con débito automático: USD $100/mes</p>
                        </Label>
                      </div>
                    )}
                    {['status_report', 'estandar'].includes(tallerActual?.nivel_suscripcion) && (
                      <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl transition-all cursor-pointer ${
                        formData.nivel_solicitado === 'estandar_pro' 
                          ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-500/20' 
                          : 'border-sky-200 hover:border-sky-300 bg-sky-50/30'
                      }`}>
                        <RadioGroupItem value="estandar_pro" id="estandar-pro-upgrade" className="text-sky-500" />
                        <Label htmlFor="estandar-pro-upgrade" className="flex-1 cursor-pointer">
                          <span className="font-semibold text-slate-800">Standard Pro</span>
                          <span className="text-sky-600 ml-2 font-bold">USD $220/mes</span>
                          <p className="text-sm text-slate-500 mt-1">5 usuarios, documentación técnica</p>
                          <p className="text-xs text-green-600 mt-1 font-medium">💳 Con débito automático: USD $200/mes</p>
                        </Label>
                      </div>
                    )}
                    {['status_report', 'estandar', 'estandar_pro'].includes(tallerActual?.nivel_suscripcion) && (
                      <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl transition-all cursor-pointer ${
                        formData.nivel_solicitado === 'premium' 
                          ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-500/20' 
                          : 'border-orange-200 hover:border-orange-300 bg-orange-50/30'
                      }`}>
                        <RadioGroupItem value="premium" id="premium-upgrade" className="text-orange-500" />
                        <Label htmlFor="premium-upgrade" className="flex-1 cursor-pointer">
                          <span className="font-semibold text-slate-800">Premium</span>
                          <span className="text-orange-600 ml-2 font-bold">USD $400/mes</span>
                          <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full ml-2">Recomendado</span>
                          <p className="text-sm text-slate-500 mt-1">10 usuarios, acceso completo</p>
                          <p className="text-xs text-green-600 mt-1 font-medium">💳 Con débito automático: USD $350/mes</p>
                        </Label>
                      </div>
                    )}
                  </RadioGroup>
                </div>

                {/* Opción de débito automático — el flujo correcto está en el Dashboard */}
                {formData.nivel_solicitado !== 'status_report' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-green-700 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                      </div>
                      <div className="text-sm">
                        <p className="font-medium text-green-800">
                          ¿Querés activar débito automático?
                        </p>
                        <p className="text-green-700 mt-1">
                          {formData.nivel_solicitado === 'estandar' && 'Ahorrá USD $20/mes (pagás $100 en vez de $120) '}
                          {formData.nivel_solicitado === 'estandar_pro' && 'Ahorrá USD $20/mes (pagás $200 en vez de $220) '}
                          {formData.nivel_solicitado === 'premium' && 'Ahorrá USD $50/mes (pagás $350 en vez de $400) '}
                          — completá primero este pago mensual y después adherite desde tu Panel con el botón{' '}
                          <span className="font-medium">"Adherir a Débito Automático"</span>.
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate('/panel')}
                          className="mt-2 text-sm font-medium text-green-800 underline hover:text-green-900"
                          data-testid="link-adherir-debito"
                        >
                          Ir al Panel para adherir →
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Checkout MercadoPago */}
                {formData.nivel_solicitado && formData.nivel_solicitado !== tallerActual?.nivel_suscripcion && (
                  <div className="mt-6">
                    <MercadoPagoCheckout
                      tipo="suscripcion"
                      titulo={`Upgrade a ${getNivelLabel(formData.nivel_solicitado)}`}
                      descripcion="Pago seguro con MercadoPago"
                      monto={
                        formData.nivel_solicitado === 'estandar'
                          ? 120
                          : formData.nivel_solicitado === 'estandar_pro'
                            ? 220
                            : 400
                      }
                      moneda="USD"
                      createPreference={async () => {
                        const response = await pagosAPI.crearPreferenciaSuscripcion({
                          nivel: formData.nivel_solicitado,
                          email: formData.email_contacto || user?.email,
                          nombre: formData.nombre_contacto || user?.full_name,
                          taller_id: user?.taller_id,
                          debito_automatico: false
                        });
                        return response.data;
                      }}
                      buttonText="Pagar y Activar"
                      onError={(error) => toast.error(error)}
                    />
                  </div>
                )}

                {/* Botón volver */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/panel')}
                    className="w-full"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Volver al Panel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <img 
                src={LOGO_LIGHT} 
                alt="C&M Servicios Aeronáuticos" 
                className="h-20 w-auto"
              />
            </div>
            <CardTitle className="text-2xl text-slate-800">{t(txt.titulo)}</CardTitle>
            <CardDescription>
              {t(txt.subtitulo)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nivel de suscripción - 3 arriba + 1 destacado abajo */}
              <div className="space-y-3">
                <Label className="text-slate-700 font-medium">{t(txt.nivelSuscripcion)}</Label>
                <RadioGroup
                  value={formData.nivel_solicitado}
                  onValueChange={(value) => handleChange('nivel_solicitado', value)}
                  className="space-y-3"
                >
                  {/* Fila 1: 3 planes básicos */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className={`flex flex-col p-4 border-2 rounded-xl transition-all cursor-pointer ${
                      formData.nivel_solicitado === 'status_report' 
                        ? 'border-slate-400 bg-slate-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`} onClick={() => handleChange('nivel_solicitado', 'status_report')}>
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="status_report" id="status_report" className="text-slate-500" />
                        <div className="flex-1">
                          <span className="font-semibold text-slate-800">Status Report</span>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-slate-600 mt-2 ml-7">{t(txtHome.gratis)}</p>
                      <p className="text-sm text-slate-500 mt-1 ml-7">1 usuario</p>
                    </div>
                    
                    <div className={`flex flex-col p-4 border-2 rounded-xl transition-all cursor-pointer ${
                      formData.nivel_solicitado === 'estandar' 
                        ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20' 
                        : 'border-teal-200 hover:border-teal-300 bg-teal-50/30'
                    }`} onClick={() => handleChange('nivel_solicitado', 'estandar')}>
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="estandar" id="estandar" className="text-teal-500" />
                        <div className="flex-1">
                          <span className="font-semibold text-slate-800">Standard</span>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-teal-600 mt-2 ml-7">USD $120/{t(txtHome.mes)}</p>
                      <p className="text-sm text-slate-500 mt-1 ml-7">2 usuarios</p>
                      <p className="text-xs text-green-600 mt-1 ml-7 font-medium">💳 Débito: $100</p>
                    </div>

                    <div className={`flex flex-col p-4 border-2 rounded-xl transition-all cursor-pointer ${
                      formData.nivel_solicitado === 'estandar_pro' 
                        ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-500/20' 
                        : 'border-sky-200 hover:border-sky-300 bg-sky-50/30'
                    }`} onClick={() => handleChange('nivel_solicitado', 'estandar_pro')}>
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="estandar_pro" id="estandar_pro" className="text-sky-500" />
                        <div className="flex-1">
                          <span className="font-semibold text-slate-800">Standard Pro</span>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-sky-600 mt-2 ml-7">USD $220/{t(txtHome.mes)}</p>
                      <p className="text-sm text-slate-500 mt-1 ml-7">5 usuarios</p>
                      <p className="text-xs text-green-600 mt-1 ml-7 font-medium">💳 Débito: $200</p>
                    </div>
                  </div>
                  
                  {/* Fila 2: Premium destacado ancho completo */}
                  <div className={`flex flex-col p-5 border-2 rounded-xl transition-all cursor-pointer relative ${
                    formData.nivel_solicitado === 'premium' 
                      ? 'border-orange-500 bg-gradient-to-r from-orange-50 to-amber-50 ring-2 ring-orange-500/30 shadow-lg' 
                      : 'border-orange-300 hover:border-orange-400 bg-gradient-to-r from-orange-50/50 to-amber-50/50 hover:shadow-md'
                  }`} onClick={() => handleChange('nivel_solicitado', 'premium')}>
                    <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white text-sm px-4 py-1 rounded-full font-medium shadow">⭐ Recomendado</span>
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-2">
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="premium" id="premium" className="text-orange-500" />
                        <span className="font-bold text-xl text-slate-800">Premium</span>
                      </div>
                      <div className="text-center sm:text-right">
                        <p className="text-2xl font-bold text-orange-600">USD $400/{t(txtHome.mes)}</p>
                        <p className="text-sm text-green-600 font-medium">💳 Con débito: USD $350/{t(txtHome.mes)}</p>
                      </div>
                      <p className="text-sm text-slate-600">10 usuarios • Acceso completo</p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Datos del taller */}
              {/* Banner de taller existente seleccionado */}
              {selectedExistingTaller && (
                <Alert className="bg-teal-50 border-teal-300 mb-4">
                  <Building2 className="h-4 w-4 text-teal-600" />
                  <AlertDescription className="text-teal-800 flex items-center justify-between">
                    <span>
                      <strong>Taller encontrado:</strong> {selectedExistingTaller.razon_social} (ANAC: {selectedExistingTaller.numero_anac})
                      <br />
                      <span className="text-sm">Se activará la suscripción para este taller existente.</span>
                    </span>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={handleClearSelection}
                      className="text-teal-700 hover:text-teal-900 hover:bg-teal-100"
                    >
                      Limpiar
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="numero_anac" className="text-slate-700 flex items-center gap-2">
                    {t(txt.numeroAnac)} *
                    {anacValidation.status === 'valid' && !selectedExistingTaller && (
                      <span className="text-xs text-green-600 font-normal flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Registrado
                      </span>
                    )}
                    {anacValidation.status === 'has_subscription' && !selectedExistingTaller && (
                      <span className="text-xs text-amber-600 font-normal flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Ya suscripto
                      </span>
                    )}
                  </Label>
                  <div className="relative">
                    <Input
                      id="numero_anac"
                      placeholder="Ej: 1B-235"
                      value={formData.numero_anac}
                      onChange={(e) => handleAnacChange(e.target.value)}
                      required
                      disabled={!!selectedExistingTaller}
                      className={`border-slate-200 focus:border-teal-500 focus:ring-teal-500 pr-10 ${
                        selectedExistingTaller ? 'bg-slate-100' : ''
                      } ${
                        anacValidation.status === 'valid' && !selectedExistingTaller ? 'border-green-400 focus:border-green-500 focus:ring-green-500' : ''
                      } ${
                        anacValidation.status === 'has_subscription' && !selectedExistingTaller ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-500' : ''
                      }`}
                      data-testid="numero-anac-input"
                    />
                    {/* Indicador de estado de validación */}
                    {!selectedExistingTaller && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {anacValidation.status === 'checking' && (
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        )}
                        {anacValidation.status === 'valid' && (
                          <CheckCircle2 className="h-4 w-4 text-green-500" data-testid="anac-valid-icon" />
                        )}
                        {anacValidation.status === 'has_subscription' && (
                          <AlertCircle className="h-4 w-4 text-amber-500" data-testid="anac-subscribed-icon" />
                        )}
                        {anacValidation.status === 'invalid' && formData.numero_anac.length >= 2 && (
                          <XCircle className="h-4 w-4 text-slate-300" data-testid="anac-invalid-icon" />
                        )}
                      </div>
                    )}
                  </div>
                  {/* Mensaje informativo debajo del campo */}
                  {anacValidation.status === 'valid' && anacValidation.tallerInfo && !selectedExistingTaller && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Taller registrado: {anacValidation.tallerInfo.razon_social}
                    </p>
                  )}
                  {anacValidation.status === 'has_subscription' && anacValidation.tallerInfo && !selectedExistingTaller && (
                    <p className="text-xs text-amber-600 mt-1">
                      Este taller ya tiene suscripción activa. Si necesitás ayuda, contactanos.
                    </p>
                  )}
                  {/* Múltiples talleres con el mismo ANAC (satélites) → el usuario debe elegir */}
                  {anacValidation.status === 'multiple' && anacValidation.talleresMultiples.length > 0 && !selectedExistingTaller && (
                    <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="anac-multiples-talleres">
                      <p className="text-sm font-medium text-blue-800 mb-2">
                        Encontramos {anacValidation.talleresMultiples.length} talleres con este número ANAC. Seleccioná el tuyo:
                      </p>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {anacValidation.talleresMultiples.map((taller) => (
                          <button
                            key={taller.id}
                            type="button"
                            onClick={() => {
                              if (taller.tiene_suscripcion) return;
                              setSelectedExistingTaller(taller);
                              setFormData(prev => ({
                                ...prev,
                                razon_social: taller.razon_social || '',
                                numero_anac: taller.numero_anac || prev.numero_anac,
                              }));
                              setAnacValidation({ status: 'valid', tallerInfo: taller, talleresMultiples: [] });
                            }}
                            disabled={taller.tiene_suscripcion}
                            className={`w-full text-left px-3 py-2 rounded border transition-colors ${
                              taller.tiene_suscripcion
                                ? 'bg-amber-50 border-amber-200 text-amber-700 cursor-not-allowed opacity-70'
                                : 'bg-white border-blue-200 hover:bg-blue-100 cursor-pointer'
                            }`}
                            data-testid={`anac-multiple-opt-${taller.id}`}
                          >
                            <div className="font-medium text-sm">{taller.razon_social}</div>
                            <div className="text-xs text-slate-500">
                              {taller.label_corto?.split('—').slice(1).join('—').trim() || taller.provincia || ''}
                              {taller.tiene_suscripcion && ' · Ya tiene suscripción activa'}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Razón social tipeada no coincide con el ANAC */}
                  {anacValidation.status === 'mismatch' && anacValidation.tallerInfo && !selectedExistingTaller && (
                    <div className="mt-2 bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="anac-mismatch-warning">
                      <p className="text-sm text-amber-800">
                        ⚠️ El ANAC <strong>{anacValidation.tallerInfo.numero_anac}</strong> corresponde a <strong>{anacValidation.tallerInfo.razon_social}</strong>, pero la razón social que escribiste es distinta.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedExistingTaller(anacValidation.tallerInfo);
                          setFormData(prev => ({
                            ...prev,
                            razon_social: anacValidation.tallerInfo.razon_social || '',
                          }));
                          setAnacValidation({ status: 'valid', tallerInfo: anacValidation.tallerInfo, talleresMultiples: [] });
                        }}
                        className="mt-2 text-sm font-medium text-amber-900 underline hover:text-amber-700"
                        data-testid="anac-mismatch-usar"
                      >
                        Usar "{anacValidation.tallerInfo.razon_social}" como mi taller
                      </button>
                    </div>
                  )}
                </div>
                <div className="space-y-2 relative">
                  <Label htmlFor="razon_social" className="text-slate-700">
                    {t(txt.razonSocial)} *
                    {!selectedExistingTaller && (
                      <span className="text-xs text-slate-500 ml-2">(Escribí para buscar tu taller)</span>
                    )}
                  </Label>
                  <div className="relative">
                    <Input
                      id="razon_social"
                      placeholder={t(txt.nombreTaller)}
                      value={formData.razon_social}
                      onChange={(e) => handleRazonSocialChange(e.target.value)}
                      onFocus={() => formData.razon_social.length >= 3 && searchResults.length > 0 && setShowAutocomplete(true)}
                      required
                      disabled={!!selectedExistingTaller}
                      className={`border-slate-200 focus:border-teal-500 focus:ring-teal-500 pr-10 ${selectedExistingTaller ? 'bg-slate-100' : ''}`}
                      autoComplete="off"
                      data-testid="razon-social-input"
                    />
                    {isSearching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                    )}
                    {!isSearching && formData.razon_social.length >= 3 && !selectedExistingTaller && (
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    )}
                  </div>
                  
                  {/* Dropdown de autocompletado */}
                  {showAutocomplete && searchResults.length > 0 && !selectedExistingTaller && (
                    <div 
                      className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto"
                      data-testid="autocomplete-dropdown"
                    >
                      <div className="p-2 text-xs text-slate-500 border-b bg-slate-50">
                        Talleres pre-registrados encontrados:
                      </div>
                      {searchResults.map((taller) => (
                        <div
                          key={taller.id}
                          onClick={() => handleSelectTaller(taller)}
                          className="p-3 hover:bg-teal-50 cursor-pointer border-b last:border-b-0 transition-colors"
                          data-testid={`autocomplete-item-${taller.id}`}
                        >
                          <div className="font-medium text-slate-800">{taller.razon_social}</div>
                          <div className="text-sm text-slate-500 flex gap-3">
                            <span>ANAC: {taller.numero_anac}</span>
                            {taller.ubicacion?.provincia && (
                              <span>• {taller.ubicacion.provincia}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Mensaje cuando no hay resultados */}
                  {showAutocomplete && searchResults.length === 0 && formData.razon_social.length >= 3 && !isSearching && !selectedExistingTaller && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm text-slate-500">
                      No se encontró un taller pre-registrado. Completá el formulario para registrarte.
                    </div>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">{t(txt.pais)} *</Label>
                  <Select value={formData.pais} onValueChange={(value) => handleChange('pais', value)}>
                    <SelectTrigger className="border-slate-200 focus:border-teal-500 focus:ring-teal-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paisesDB.map(p => (
                        <SelectItem key={p.id} value={p.nombre}>
                          <span className="flex items-center gap-2">
                            <img src={`https://flagcdn.com/w20/${p.codigo.toLowerCase()}.png`} alt={p.codigo} className="w-5 h-auto" />
                            {p.nombre}
                          </span>
                        </SelectItem>
                      ))}
                      <SelectItem value="Otro">{language === 'pt' ? 'Outro país' : 'Otro país'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">{t(txt.provincia)} *</Label>
                  <Select value={formData.provincia} onValueChange={(value) => handleChange('provincia', value)}>
                    <SelectTrigger className="border-slate-200 focus:border-teal-500 focus:ring-teal-500">
                      <SelectValue placeholder={t(txt.seleccionar)} />
                    </SelectTrigger>
                    <SelectContent>
                      {getProvincias().map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Datos de contacto */}
              <div className="space-y-2">
                <Label htmlFor="nombre_contacto" className="text-slate-700">{t(txt.nombreContacto)} *</Label>
                <Input
                  id="nombre_contacto"
                  placeholder={t(txt.nombreCompleto)}
                  value={formData.nombre_contacto}
                  onChange={(e) => handleChange('nombre_contacto', e.target.value)}
                  required
                  className="border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email_contacto" className="text-slate-700">{t(txt.email)} *</Label>
                  <Input
                    id="email_contacto"
                    type="email"
                    placeholder={language === 'pt' ? 'email@exemplo.com' : 'email@ejemplo.com'}
                    value={formData.email_contacto}
                    onChange={(e) => handleChange('email_contacto', e.target.value)}
                    required
                    className="border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono_contacto" className="text-slate-700">{t(txt.telefono)} *</Label>
                  <Input
                    id="telefono_contacto"
                    placeholder={language === 'pt' ? '+55 ...' : '+54 ...'}
                    value={formData.telefono_contacto}
                    onChange={(e) => handleChange('telefono_contacto', e.target.value)}
                    required
                    className="border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notas" className="text-slate-700">{t(txt.notasAdicionales)}</Label>
                <Textarea
                  id="notas"
                  placeholder={t(txt.notasPlaceholder)}
                  value={formData.notas}
                  onChange={(e) => handleChange('notas', e.target.value)}
                  rows={3}
                  className="border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>

              {formData.nivel_solicitado !== 'status_report' && (
                <Alert className="bg-teal-50 border-teal-200">
                  <AlertDescription className="text-teal-800">
                    {t(txt.alertaPago)}
                  </AlertDescription>
                </Alert>
              )}

              {/* Términos y Condiciones */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="terminos" 
                    checked={terminosAceptados}
                    onCheckedChange={setTerminosAceptados}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor="terminos" 
                      className="text-sm text-slate-700 cursor-pointer"
                    >
                      {t(txt.aceptoTerminos)}{' '}
                      <button
                        type="button"
                        onClick={() => setShowTerminos(true)}
                        className="text-teal-600 hover:text-teal-700 font-medium underline inline-flex items-center gap-1"
                      >
                        <FileText className="h-3 w-3" />
                        {t(txt.terminosCondiciones)}
                      </button>
                    </label>
                    <p className="text-xs text-slate-500 mt-1">
                      Premium $400 | Standard $120 ($100 {language === 'pt' ? 'débito auto' : 'débito auto'}) | Status Report {t(txtHome.gratis)}
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/25" 
                disabled={loading || !terminosAceptados}
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t(txt.enviando)}</>
                ) : (
                  t(txt.enviarSolicitud)
                )}
              </Button>

              {!terminosAceptados && (
                <p className="text-xs text-center text-slate-500">
                  {t(txt.debesAceptar)}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Sección para Talleres Itinerantes / Sin Certificación */}
      <div className="mt-12 bg-gradient-to-r from-sky-50 to-teal-50 rounded-2xl p-8 border border-sky-200">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-100 rounded-full mb-4">
            <Plane className="h-8 w-8 text-sky-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-3">
            ¿Taller Itinerante o Sin Certificación Específica?
          </h3>
          <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
            Si sos un taller aeronáutico itinerante o no contás con una certificación de capacidad 
            operativa específica emitida por la autoridad aeronáutica nacional, 
            podemos diseñar un <strong>plan personalizado</strong> según tus necesidades.
          </p>
          <Button 
            onClick={() => setShowFormularioItinerante(true)}
            className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-3 text-lg"
          >
            <Mail className="h-5 w-5 mr-2" />
            Solicitar Suscripción Personalizada
          </Button>
        </div>
      </div>

      {/* Modal de Términos y Condiciones */}
      <TerminosCondicionesModal 
        open={showTerminos} 
        onOpenChange={setShowTerminos}
        onAccept={() => setTerminosAceptados(true)}
      />

      {/* Formulario para Talleres Itinerantes */}
      <FormularioTallerItinerante 
        isOpen={showFormularioItinerante}
        onClose={() => setShowFormularioItinerante(false)}
      />
    </div>
  );
};

export default SuscripcionPage;
